import { getSubjectLabels } from '@/lib/settings';
import { useEffect, useMemo, useState } from 'react';
import { Filter, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useStandort } from '@/components/layouts/use-standort';
import MigrationCaseGridCard from '@/components/migration/MigrationCaseGridCard';
import MigrationCaseLinkModal from '@/components/migration/MigrationCaseLinkModal';
import MigrationCaseActionWizardModal from '@/components/migration/MigrationCaseActionWizardModal';
import MigrationCaseHistoryModal from '@/components/migration/MigrationCaseHistoryModal';
import MigrationOfferWizardModal from '@/components/migration/MigrationOfferWizardModal';
import ConfirmOrderWizardModal from '@/components/trials/ConfirmOrderWizardModal';
import StartCourseWizardModal from '@/components/trials/StartCourseWizardModal';
import MessageWizardModal, { type MessageRecipient } from '@/components/communications/MessageWizardModal';
import type { TrialLessonDTO } from '@/lib/trials';
import { migrationApi } from '@/lib/api';
import { useMigrationCases } from '@/hooks/useMigrationCases';
import { getWizardMissingSteps, getWizardSnapshot } from '@/lib/migrationCaseWizard';
import { toast } from 'sonner';

type CaseFilterStatus =
  | 'all'
  | 'vertragsanpassung_offen'
  | 'auftragsanpassung_offen'
  | 'angebot_offen'
  | 'angebot_versendet'
  | 'unterricht_bestaetigen'
  | 'kurs_starten'
  | 'vertrag_beenden_offen'
  | 'abgeschlossen';
type WizardFilterStatus = 'all' | 'not_started' | 'open' | 'complete';
type ActionWizardType = 'contract_adjustment' | 'contract_termination' | 'order_adjustment' | null;

const buildWhatsAppLink = (phone: string, text: string): string => {
  const digits = phone.replaceAll(/\D/g, '');
  if (!digits) return '';
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

const resolveLegacyId = (raw: unknown): number => {
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : 0;
};

const toSafeString = (value: unknown): string => (typeof value === 'string' ? value : '');

const resolveCaseTeacher = (item: { legacy_contract?: { teacher?: { id?: number | null; name?: string | null } | null } | null; snapshot?: Record<string, unknown> | null }) => {
  const contractTeacher = item.legacy_contract?.teacher;
  const snapshot = (item.snapshot as Record<string, unknown> | null) ?? {};
  const id = resolveLegacyId(contractTeacher?.id ?? snapshot.legacy_teacher_id);
  const name = toSafeString(contractTeacher?.name) || toSafeString(snapshot.legacy_teacher_name);
  return { id, name };
};

const resolveCaseFachId = (item: { legacy_contract?: { fach?: { id?: number | null } | null } | null; snapshot?: Record<string, unknown> | null }) => {
  const snapshot = (item.snapshot as Record<string, unknown> | null) ?? {};
  return resolveLegacyId(item.legacy_contract?.fach?.id ?? snapshot.legacy_fach_id);
};

const resolveCaseUformId = (item: { legacy_contract?: { unterrichtsform?: { id?: number | null } | null } | null; snapshot?: Record<string, unknown> | null }) => {
  const snapshot = (item.snapshot as Record<string, unknown> | null) ?? {};
  return resolveLegacyId(item.legacy_contract?.unterrichtsform?.id ?? snapshot.legacy_uform_id);
};

const matchesQuery = (item: { legacy_contract?: { student?: { first_name?: string | null; last_name?: string | null } | null; contract_partner?: { name?: string | null } | null; fach?: { name?: string | null } | null; teacher?: { name?: string | null } | null } | null; wp_user_name?: string | null; wp_user_email?: string | null }, query: string): boolean => {
  if (!query) return true;
  const haystack = [
    item.legacy_contract?.student?.first_name,
    item.legacy_contract?.student?.last_name,
    item.legacy_contract?.contract_partner?.name,
    item.legacy_contract?.fach?.name,
    item.legacy_contract?.teacher?.name,
    item.wp_user_name,
    item.wp_user_email,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());
  return haystack.some((value) => value.includes(query));
};

/**
 * Baut einen synthetischen TrialLessonDTO aus einem Migration-Case-Snapshot.
 * ConfirmOrderWizardModal ist auf einen Trial-DTO ausgelegt — Migration-Cases
 * haben keine Probestunde, also synthetisieren wir die Anzeige-Daten aus
 * office_contract_adjustment.payload.lesson + finalization. Das Enrollment
 * selbst laedt der Modal ueber enrollmentIdOverride direkt per ID.
 */
const buildMigrationConfirmTrial = (
  item: { id: number; standort_id?: number; subprofile_id?: string | null; snapshot?: Record<string, unknown> | null }
): { trial: TrialLessonDTO; enrollmentId: number } | null => {
  const snapshot = (item.snapshot ?? {}) as Record<string, any>;
  const office = (snapshot.office_contract_adjustment?.payload ?? {}) as Record<string, any>;
  const lesson = (office.lesson ?? {}) as Record<string, any>;
  const student = (office.legacy_updates?.student ?? {}) as Record<string, any>;
  const finalization = (snapshot.customer_contract_adjustment?.finalization ?? {}) as Record<string, any>;
  const orderId = Number(finalization.order_id ?? 0);
  const enrollmentId = Number(finalization.enrollment_id ?? 0);
  if (orderId <= 0 || enrollmentId <= 0) return null;

  const startTime = String(lesson.start_time ?? '').slice(0, 5);
  const durationMin = Number(lesson.duration_min ?? 30) || 30;
  let endTime = '';
  if (startTime && startTime.includes(':')) {
    const [h, m] = startTime.split(':').map(Number);
    if (Number.isFinite(h) && Number.isFinite(m)) {
      const endM = h * 60 + m + durationMin;
      endTime = `${String(Math.floor(endM / 60) % 24).padStart(2, '0')}:${String(endM % 60).padStart(2, '0')}`;
    }
  }
  const studentName = `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim()
    || `${snapshot.legacy_student_first_name ?? ''} ${snapshot.legacy_student_last_name ?? ''}`.trim()
    || 'Schüler';

  const trial = {
    id: `migration-${item.id}`,
    studentName,
    teacherId: lesson.teacher_wp_user_id ? String(lesson.teacher_wp_user_id) : undefined,
    teacherName: String(lesson.teacher_name ?? snapshot.legacy_teacher_name ?? ''),
    fachName: String(lesson.fach_name ?? snapshot.legacy_fach_name ?? ''),
    date: String(office.start_date ?? snapshot.legacy_begin ?? ''),
    startTime: startTime ? `${startTime}:00` : '',
    endTime: endTime ? `${endTime}:00` : '',
    status: 'bestätigt',
    roomName: String(lesson.room_name ?? ''),
    subprofileId: (item.subprofile_id ?? finalization.subprofile_id ?? null) as string | null,
    order: { id: orderId, status: 'angenommen' },
    // standort_id liest der Modal via (trial as any) — kein DTO-Feld.
    standort_id: item.standort_id ?? 0,
  } as unknown as TrialLessonDTO;
  return { trial, enrollmentId };
};

const getCaseContact = (item: { snapshot?: Record<string, unknown> | null; wp_user_email?: string | null; wp_user_name?: string | null; email_check?: { candidate?: string | null } | null; legacy_contract?: { student?: { first_name?: string | null; last_name?: string | null } | null } | null }) => {
  const snapshot = (item.snapshot as Record<string, unknown> | null) ?? {};
  const officeWizard = (snapshot.office_wizard as Record<string, unknown> | null) ?? {};
  const stepData = (officeWizard.step_data as Record<string, unknown> | null) ?? {};
  const dataCheck = (stepData.data_check as Record<string, unknown> | null) ?? {};
  const userProfile = (dataCheck.user_profile as Record<string, unknown> | null) ?? {};
  const email = (toSafeString(userProfile.email) || toSafeString(item.wp_user_email) || toSafeString(item.email_check?.candidate)).trim();
  // Telefonnummer: bevorzugt aus dem Office-Datencheck, sonst aus den
  // importierten Snapshot-Feldern (Vertragspartner zuerst — er ist der
  // Account-Inhaber/Ansprechpartner — dann Schüler).
  const phone = (
    toSafeString(userProfile.phone)
    || toSafeString(snapshot.legacy_contract_partner_phone)
    || toSafeString(snapshot.legacy_student_phone)
  ).trim();
  const student = item.legacy_contract?.student;
  const fallbackName = [student?.last_name, student?.first_name].filter(Boolean).join(', ').trim();
  const name = String(item.wp_user_name ?? fallbackName ?? '').trim();
  const tel = phone ? `tel:${phone}` : '';
  const wa = phone ? buildWhatsAppLink(phone, 'Hallo!') : '';
  return { email, phone, tel, wa, name };
};

const MigrationCases = () => {
  const { selectedStandortId, selectedStandort } = useStandort();
  const {
    cases,
    error,
    isLoadingCases,
    searchCases,
    getOpenSummary,
    setFavorite,
    removeFavorite,
    listFavorites,
    previewContractAdjustment,
    executeContractAdjustment,
    previewContractTermination,
    executeContractTermination,
    previewOrderAdjustment,
    executeOrderAdjustment,
    getCaseDetails,
  } = useMigrationCases();

  const [isBusy, setIsBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [caseFilter, setCaseFilter] = useState<CaseFilterStatus>('all');
  const [wizardFilter, setWizardFilter] = useState<WizardFilterStatus>('all');
  const [teacherFilter, setTeacherFilter] = useState<string>('0');
  const [fachFilter, setFachFilter] = useState<string>('0');
  const [uformFilter, setUformFilter] = useState<string>('0');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [linkCaseId, setLinkCaseId] = useState<number | null>(null);
  const [actionCaseId, setActionCaseId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<ActionWizardType>(null);
  const [offerWizardCaseId, setOfferWizardCaseId] = useState<number | null>(null);
  const [confirmOrderState, setConfirmOrderState] = useState<{ trial: TrialLessonDTO; enrollmentId: number } | null>(null);
  const [startCourseEnrollmentId, setStartCourseEnrollmentId] = useState<number | null>(null);
  const [allowContractTermination, setAllowContractTermination] = useState(false);
  const [messageRecipients, setMessageRecipients] = useState<MessageRecipient[] | null>(null);
  const [historyCaseId, setHistoryCaseId] = useState<number | null>(null);

  useEffect(() => {
    const handle = globalThis.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => globalThis.clearTimeout(handle);
  }, [search]);

  const reloadCases = async () => {
    if (!selectedStandortId) return;
    const items = await searchCases({
      standort_id: selectedStandortId,
      status: caseFilter === 'all' ? undefined : caseFilter,
      favorites_only: favoritesOnly || undefined,
      limit: 200,
      offset: 0,
    });
    setFavoriteIds(items.filter((i) => i.is_favorite).map((i) => i.id));
    return items;
  };

  const reloadCasesVoid = async (): Promise<void> => {
    await reloadCases();
  };

  useEffect(() => {
    if (!selectedStandortId) return;
    void reloadCases();
    void getOpenSummary(selectedStandortId).then((s) => setOpenCount(s.count ?? 0)).catch(() => setOpenCount(0));
    void listFavorites(selectedStandortId).then((ids) => setFavoriteIds(ids)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStandortId, caseFilter, favoritesOnly]);

  useEffect(() => {
    void migrationApi
      .getSettings()
      .then((settings) => setAllowContractTermination(!!settings.allowContractTermination))
      .catch(() => setAllowContractTermination(false));
  }, []);

  const teacherOptions = useMemo(() => {
    const map = new Map<number, string>();
    cases.forEach((c) => {
      const teacher = resolveCaseTeacher(c);
      if (teacher.id > 0 && teacher.name) map.set(teacher.id, teacher.name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [cases]);

  const fachOptions = useMemo(() => {
    const map = new Map<number, string>();
    cases.forEach((c) => {
      const id = resolveCaseFachId(c);
      const name = String(c.legacy_contract?.fach?.name ?? '').trim();
      if (id > 0) map.set(id, name || `${getSubjectLabels().singular} #${id}`);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [cases]);

  const uformOptions = useMemo(() => {
    const map = new Map<number, string>();
    cases.forEach((c) => {
      const id = resolveCaseUformId(c);
      const name = String(c.legacy_contract?.unterrichtsform?.name ?? '').trim();
      if (id > 0) map.set(id, name || `U-Form #${id}`);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [cases]);

  const filteredCases = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return cases.filter((item) => {
      if (teacherFilter !== '0') {
        const teacher = resolveCaseTeacher(item);
        if (teacher.id !== Number(teacherFilter)) return false;
      }
      if (fachFilter !== '0' && resolveCaseFachId(item) !== Number(fachFilter)) return false;
      if (uformFilter !== '0' && resolveCaseUformId(item) !== Number(uformFilter)) return false;
      if (!matchesQuery(item, q)) return false;
      if (wizardFilter === 'all') return true;
      const wizard = getWizardSnapshot(item.snapshot);
      const missingSteps = getWizardMissingSteps(wizard);
      if (wizardFilter === 'not_started') return !wizard;
      if (wizardFilter === 'open') return !!wizard && missingSteps.length > 0;
      if (wizardFilter === 'complete') return !!wizard && missingSteps.length === 0;
      return true;
    });
  }, [cases, wizardFilter, teacherFilter, fachFilter, uformFilter, debouncedSearch]);

  const onToggleFavorite = async (caseId: number, nextFavorite: boolean) => {
    setIsBusy(true);
    setSuccessMessage(null);
    try {
      if (nextFavorite) {
        await setFavorite(caseId);
      } else {
        await removeFavorite(caseId);
      }
      await reloadCases();
      setSuccessMessage(nextFavorite ? 'Favorit gesetzt.' : 'Favorit entfernt.');
    } finally {
      setIsBusy(false);
    }
  };

  const openActionWizard = (caseId: number, type: ActionWizardType) => {
    if (type === 'contract_adjustment') {
      const target = cases.find((item) => item.id === caseId);
      const hasUnresolvedEmailConflict = !!target?.email_exists_in_system && !(target?.is_linked_or_created || target?.person_detail_wp_user_id);
      if (hasUnresolvedEmailConflict) {
        toast.error('Vertragsanpassung erst nach Verknüpfung oder Neuanlage des Datensatzes möglich.');
        return;
      }
    }
    setActionCaseId(caseId);
    setActionType(type);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">offene Migrationen</h1>
        <p className="text-muted-foreground">
          offene Migrationen als Kartenansicht für Standort {selectedStandort?.name ?? '-'} ({openCount} offen).
        </p>
      </div>

      {(error || successMessage) ? (
        <div
          className={
            error
              ? 'rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200'
              : 'rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200'
          }
        >
          {error ?? successMessage}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/70 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Filter className="h-3.5 w-3.5" />
            Filter
          </div>
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={caseFilter}
            onChange={(e) => setCaseFilter(e.target.value as CaseFilterStatus)}
          >
            <option value="all">Alle Prozessphasen</option>
            <option value="vertragsanpassung_offen">Vertragsanpassung offen</option>
            <option value="auftragsanpassung_offen">Kunde im Portal</option>
            <option value="angebot_offen">Angebot an Lehrkraft offen</option>
            <option value="angebot_versendet">Angebot bei der Lehrkraft</option>
            <option value="unterricht_bestaetigen">Unterricht bestätigen</option>
            <option value="kurs_starten">Kurs starten</option>
            <option value="vertrag_beenden_offen">Vertrag beenden offen</option>
            <option value="abgeschlossen">Abgeschlossen</option>
          </select>
          <select className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={teacherFilter} onChange={(e) => setTeacherFilter(e.target.value)}>
            <option value="0">Lehrkraft: alle</option>
            {teacherOptions.map((teacher) => (
              <option key={teacher.id} value={String(teacher.id)}>
                {teacher.name}
              </option>
            ))}
          </select>
          <select className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={fachFilter} onChange={(e) => setFachFilter(e.target.value)}>
            <option value="0">{getSubjectLabels().singular}: alle</option>
            {fachOptions.map((fach) => (
              <option key={fach.id} value={String(fach.id)}>
                {fach.name}
              </option>
            ))}
          </select>
          <select className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={uformFilter} onChange={(e) => setUformFilter(e.target.value)}>
            <option value="0">Unterrichtsform: alle</option>
            {uformOptions.map((uform) => (
              <option key={uform.id} value={String(uform.id)}>
                {uform.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm">
            <input type="checkbox" checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)} />
            <span>Nur Favoriten</span>
          </label>
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={wizardFilter}
            onChange={(e) => setWizardFilter(e.target.value as WizardFilterStatus)}
          >
            <option value="all">Wizard: Alle</option>
            <option value="not_started">Wizard: Nicht gestartet</option>
            <option value="open">Wizard: Offen</option>
            <option value="complete">Wizard: Vollständig</option>
          </select>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              className="w-64 rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
              placeholder="Livesuche nach Schülername"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <Button variant="outline" onClick={() => void reloadCases()} disabled={!selectedStandortId || isBusy || isLoadingCases}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Aktualisieren
        </Button>
      </div>

      {isLoadingCases ? <p className="text-sm text-muted-foreground">Lade Migrationsfälle...</p> : null}

      <div className="space-y-3">
        {filteredCases.map((item) => (
          <MigrationCaseGridCard
            key={item.id}
            item={{ ...item, is_favorite: favoriteIds.includes(item.id) || item.is_favorite }}
            isBusy={isBusy}
            allowContractTermination={allowContractTermination}
            onLink={(caseId) => setLinkCaseId(caseId)}
            onStartContractAdjustment={(caseId) => openActionWizard(caseId, 'contract_adjustment')}
            onStartContractTermination={(caseId) => openActionWizard(caseId, 'contract_termination')}
            onStartOrderAdjustment={(caseId) => openActionWizard(caseId, 'order_adjustment')}
            onSendOrderOffer={(caseId, _orderId) => {
              // Öffnet den Angebots-Wizard (3 Schritte: Vorschau → Mail → Übersicht).
              // Der Wizard ruft am Ende migrationApi.sendCaseOffer auf, das
              // idempotent Enrollment + Order sicherstellt und die Mail mit
              // custom Subject/HTML versendet.
              setOfferWizardCaseId(caseId);
            }}
            onConfirmOrder={(caseId) => {
              // "Unterricht bestätigen" — 1:1 wie im Probestunden-Flow:
              // oeffnet den ConfirmOrderWizardModal mit Auftrags-/Zahlungs-
              // Uebersicht. Migration-Cases haben keine Probestunde, daher
              // bauen wir einen synthetischen Trial-DTO aus dem Case-Snapshot;
              // das Enrollment laedt der Modal via enrollmentIdOverride.
              const target = filteredCases.find((c) => c.id === caseId);
              if (!target) return;
              const built = buildMigrationConfirmTrial(target);
              if (!built) {
                toast.error('Auftrag/Anmeldung zum Case nicht gefunden.');
                return;
              }
              setConfirmOrderState(built);
            }}
            onStartCourse={(_caseId, enrollmentId) => {
              // „Kurs starten“ wie im Probestunden-Flow: Kursteilnahme anlegen und den
              // reservierten Kurs aktivieren. Danach ist der Fall migriert.
              if (enrollmentId > 0) setStartCourseEnrollmentId(enrollmentId);
            }}
            onToggleFavorite={(caseId, next) => void onToggleFavorite(caseId, next)}
            onCall={(caseId) => {
              const target = filteredCases.find((c) => c.id === caseId);
              if (!target) return;
              const contact = getCaseContact(target);
              if (!contact.tel) {
                toast.error('Keine Telefonnummer vorhanden');
                return;
              }
              globalThis.location.href = contact.tel;
            }}
            onCallFailed={async (caseId) => {
              const target = filteredCases.find((c) => c.id === caseId);
              const label = (target ? getCaseContact(target).name : '') || `Case #${caseId}`;
              try {
                await migrationApi.recordContactAttempt(caseId, 'not_reached');
                toast.info(`Als nicht erreicht markiert: ${label}`);
                // Latest-Event-Pill aktualisieren.
                void reloadCases();
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Ereignis konnte nicht gespeichert werden.');
              }
            }}
            onCommunicate={(caseId) => {
              const target = filteredCases.find((c) => c.id === caseId);
              if (!target) return;
              const contact = getCaseContact(target);
              const wpUserId = Number(target.wp_user_id ?? 0);
              if (wpUserId > 0) {
                // In-App-Nachrichten-Fenster statt mailto.
                setMessageRecipients([{
                  id: `person:${wpUserId}`,
                  type: 'person',
                  wpUserId,
                  name: contact.name || 'Kunde',
                  email: contact.email || null,
                  phone: contact.phone || null,
                }]);
                return;
              }
              // VA-6: Unverknüpfter Case → KEIN mailto-Fallback mehr; das
              // In-App-Nachrichten-Fenster braucht einen wp_user. Office muss
              // den Datensatz zuerst verknüpfen.
              toast.error('Bitte zuerst den Datensatz verknüpfen, um eine Nachricht zu senden.');
            }}
            onAddComment={async (caseId, text) => {
              try {
                await migrationApi.addCaseComment(caseId, text);
                toast.success('Kommentar gespeichert.');
                // Latest-Event-Pill aktualisieren.
                void reloadCases();
                return true;
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Kommentar konnte nicht gespeichert werden.');
                return false;
              }
            }}
            onOpenHistory={(caseId) => setHistoryCaseId(caseId)}
          />
        ))}
        {filteredCases.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
            Keine Cases für die aktuelle Auswahl.
          </div>
        ) : null}
      </div>

      <MigrationCaseLinkModal
        open={linkCaseId !== null}
        caseId={linkCaseId}
        onOpenChange={(isOpen) => {
          if (!isOpen) setLinkCaseId(null);
        }}
        onLinked={reloadCasesVoid}
      />
      <MigrationCaseActionWizardModal
        open={actionCaseId !== null && actionType !== null}
        caseId={actionCaseId}
        actionType={actionType ?? 'contract_adjustment'}
        loadCaseDetails={getCaseDetails}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setActionCaseId(null);
            setActionType(null);
          }
        }}
        onPreview={(caseId, payload) => {
          if (actionType === 'contract_termination') return previewContractTermination(caseId, payload);
          if (actionType === 'order_adjustment') return previewOrderAdjustment(caseId, payload);
          return previewContractAdjustment(caseId, payload);
        }}
        onExecute={(caseId, payload) => {
          if (actionType === 'contract_termination') return executeContractTermination(caseId, payload);
          if (actionType === 'order_adjustment') return executeOrderAdjustment(caseId, payload);
          return executeContractAdjustment(caseId, payload);
        }}
        onDone={reloadCasesVoid}
      />

      <MigrationOfferWizardModal
        open={offerWizardCaseId !== null}
        caseItem={offerWizardCaseId !== null ? filteredCases.find((c) => c.id === offerWizardCaseId) ?? null : null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setOfferWizardCaseId(null);
        }}
        onDone={reloadCasesVoid}
      />

      <MessageWizardModal
        open={messageRecipients !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setMessageRecipients(null);
        }}
        standortId={selectedStandortId ? Number(selectedStandortId) : 0}
        title="Nachricht an Kunde"
        recipients={messageRecipients ?? []}
        initialChannel="email"
        templateKeys={{
          email: 'communications.manual.person',
          sms: 'communications.manual.person',
        }}
        initialDrafts={{
          email: {
            subjectTpl: 'Nachricht von {{schoolName}}',
            htmlTpl: '<p>Hallo {{recipientName}},</p><p></p><p>Viele Grüße<br>{{schoolName}}</p>',
            textTpl: 'Hallo {{recipientName}},\n\n\nViele Grüße\n{{schoolName}}',
          },
          sms: { smsTpl: 'Hallo {{recipientName}}, ' },
        }}
      />

      <MigrationCaseHistoryModal
        open={historyCaseId !== null}
        caseId={historyCaseId}
        onOpenChange={(isOpen) => {
          if (!isOpen) setHistoryCaseId(null);
        }}
      />

      {confirmOrderState ? (
        <ConfirmOrderWizardModal
          open={confirmOrderState !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) setConfirmOrderState(null);
          }}
          trial={confirmOrderState.trial}
          enrollmentIdOverride={confirmOrderState.enrollmentId}
          onConfirmed={async () => {
            toast.success('Unterricht bestätigt');
            setConfirmOrderState(null);
            await reloadCases();
          }}
        />
      ) : null}

      <StartCourseWizardModal
        open={startCourseEnrollmentId !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setStartCourseEnrollmentId(null);
        }}
        enrollmentId={startCourseEnrollmentId ?? 0}
        onComplete={async () => {
          toast.success('Kurs gestartet');
          setStartCourseEnrollmentId(null);
          await reloadCases();
        }}
      />
    </div>
  );
};

export default MigrationCases;
